import { ui } from '@beelday/common'
import { FC, useEffect, useState } from 'react'
import { ConnectionQuality } from 'connectivity/awaited-connections'
import OfflineFriendlyInfoBanner from 'interaction-scheme/presentation/offline-friendly-info-banner'
import { useSelector } from 'react-redux'
import { RootState } from 'common/redux'
import useIsTrainer from 'room/common/use-is-trainer'
import { useOptionalRecordingState } from 'features/recording/recording-context'
import { useSessionMode } from 'session-mode/use-session-mode'
import { PHONE_TOP_BAR_HEIGHT } from 'room/common/presentation/in-person-phone'

const SystemNotifications: FC = () => {
	const connectionQuality = useSelector(
		(state: RootState) => state.connectivity.quality
	)
	const isTrainer = useIsTrainer()
	const recordingState = useOptionalRecordingState()
	const inPerson = useSessionMode() === 'IN_PERSON'
	const isDesktop = ui.responsive.useIsDesktop1024()
	const badConnection = connectionQuality === ConnectionQuality.Bad
	// In person there is no video, so the "lower quality video" copy is wrong.
	const connectionWarningKey = inPerson
		? 'bad_connection_warning_in_person'
		: 'bad_connection_warning'
	// The participant's phone shell keeps its top bar - and with it the menu -
	// reachable: notifications open below it rather than over it.
	const baseOffset =
		inPerson && !isTrainer && !isDesktop ? PHONE_TOP_BAR_HEIGHT : 0

	// Closed until the connection recovers; the next bad spell shows it again.
	const [connectionDismissed, setConnectionDismissed] = useState(false)
	useEffect(() => {
		if (!badConnection) {
			setConnectionDismissed(false)
		}
	}, [badConnection])
	const showConnectionWarning = badConnection && !connectionDismissed
	const recordingMessageKey = recordingState?.uiState.messageKey
	const [dismissedMessageKey, setDismissedMessageKey] = useState<
		string | undefined
	>(undefined)

	useEffect(() => {
		setDismissedMessageKey(undefined)
	}, [recordingMessageKey])

	const showRecordingWarning =
		isTrainer &&
		!!recordingState?.uiState.showPersistentNotification &&
		!!recordingMessageKey &&
		dismissedMessageKey !== recordingMessageKey

	return (
		<>
			{showConnectionWarning ? (
				<OfflineFriendlyInfoBanner
					message={connectionWarningKey}
					onDismiss={() => setConnectionDismissed(true)}
					topOffset={baseOffset}
				/>
			) : null}
			{showRecordingWarning ? (
				<OfflineFriendlyInfoBanner
					actionLabel={
						recordingState.uiState.canReshare
							? 'room_menu.recording.reshare'
							: undefined
					}
					message={recordingState.uiState.messageKey}
					onAction={
						recordingState.uiState.canReshare
							? recordingState.reshareRecording
							: undefined
					}
					onDismiss={() => setDismissedMessageKey(recordingMessageKey)}
					tone={recordingState.uiState.severity}
					topOffset={baseOffset + (showConnectionWarning ? 88 : 0)}
				/>
			) : null}
		</>
	)
}

export default SystemNotifications
